// @ts-check
import { toPriceObject } from '../utils/price';
import { formatCurrency as fmt } from '../utils/currency';

/**
 * @typedef {{ retail?: number|null, educator?: number|null, school?: number|null, currency?: string }} PriceObj
 * @typedef {{
 *   isPriced: boolean,
 *   displayLabel: string,
 *   originalLabel: string|null,
 *   canCheckout: boolean,
 *   canQuote: boolean,
 *   raw: number|null,
 *   currency: string,
 * }} PriceViewModel
 */


/**
 * The single price rendering contract for the entire storefront.
 *
 * NEVER throws. NEVER returns null properties.
 * When price is missing/null: returns a safe "Price on request" view model with
 * canCheckout=false so add-to-cart CTAs are automatically replaced by quote CTAs.
 *
 * @param {PriceObj|null|undefined} priceObj
 * @param {'retail'|'educator'|'school'} [segment]
 * @param {PriceObj|null|undefined} [originalPriceObj] - previous/crossed-out price
 * @returns {PriceViewModel}
 */
export const adaptPrice = (priceObj, segment = 'retail', originalPriceObj = null) => {
  const normalized = toPriceObject(priceObj, 'USD');
  const normalizedOriginal = toPriceObject(originalPriceObj, normalized?.currency || 'USD');
  const currency = normalized?.currency || 'USD';
  const raw = normalized?.[segment] ?? null;

  if (raw === null || raw === undefined || raw <= 0) {
    return {
      isPriced: false,
      displayLabel: 'Price on request',
      originalLabel: null,
      canCheckout: false,
      canQuote: true,
      raw: null,
      currency,
    };
  }

  const origRaw = normalizedOriginal?.[segment] ?? null;

  return {
    isPriced: true,
    displayLabel: fmt(raw, currency),
    originalLabel: origRaw !== null && origRaw > raw ? fmt(origRaw, currency) : null,
    canCheckout: true,
    canQuote: false,
    raw,
    currency,
  };
};
